import { useEffect, useState } from 'react';
import { HubClientError } from '@verybigthings/trackframe-hub-client';
import type { IdentityConfig, TfUserRow, TrackLevel } from '@verybigthings/trackframe-shared/schemas';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { hub } from '@/lib/hub-client';
import { LevelSelect, RoleCheckList, toggleInSet, type RoleRef } from '../tracks/access/grant-fields';

/** Invites need a mail provider that actually sends (HUB-106). */
export function inviteEnabled(config: IdentityConfig | null): boolean {
  return config !== null && config.emailProvider !== 'noop';
}

/** A link can be re-sent until the user is active (has a password) or disabled. */
export function canResendInvite(user: Pick<TfUserRow, 'hasPassword' | 'disabledAt'>): boolean {
  return !user.hasPassword && user.disabledAt === null;
}

/** Optional track grant written with the invite, before the mail (HUB-109 D7). */
export interface InviteGrant {
  trackId: string;
  level: TrackLevel | null;
  roleIds: string[];
}

/** `{}` without a grant — wire-equivalent to the pre-D7 params-only call. */
export function inviteBody(grant?: InviteGrant): { trackId?: string; level?: TrackLevel; roleIds?: string[] } {
  if (!grant) return {};
  return { trackId: grant.trackId, ...(grant.level ? { level: grant.level } : {}), roleIds: grant.roleIds };
}

export async function sendInvite(userId: string, grant?: InviteGrant): Promise<void> {
  await hub.usersInvite({ params: { id: userId }, body: inviteBody(grant) });
}

function inviteErrorMessage(err: unknown): string {
  if (err instanceof HubClientError) {
    if (err.code === 'email_taken') return 'A user with this email already exists.';
    if (err.code === 'email_unconfigured') return 'Email sending is off. Configure it under Settings → email.';
    if (err.code === 'email_send_failed') return 'The invite email could not be sent. Check the SMTP settings and try again.';
  }
  return (err as Error).message ?? 'Invite failed';
}

export interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
  onInvited: (email: string) => void;
  /** When set, the invite also grants access to this track (level + roles). */
  trackId?: string;
  trackName?: string;
  trackRoles?: RoleRef[];
}

export function InviteUserDialog({ open, onClose, onInvited, trackId, trackName, trackRoles }: InviteUserDialogProps) {
  const [email, setEmail] = useState('');
  const [level, setLevel] = useState<TrackLevel | null>(null);
  const [roleIds, setRoleIds] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The user row outlives a failed send: a retry after "could not be sent"
  // only re-sends, it never re-creates (which would answer email_taken).
  const [created, setCreated] = useState<{ id: string; email: string } | null>(null);

  useEffect(() => {
    if (!open) return;
    setLevel(null);
    setRoleIds(new Set());
  }, [open]);

  const close = () => {
    setEmail('');
    setError(null);
    setCreated(null);
    onClose();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const user = created ?? (await hub.usersCreate({ body: { email: email.trim() } })).user;
      setCreated(user);
      await sendInvite(user.id, trackId ? { trackId, level, roleIds: [...roleIds] } : undefined);
      onInvited(user.email);
      close();
    } catch (err) {
      setError(inviteErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      title={trackId ? `Invite to ${trackName ?? 'track'}` : 'Invite user'}
      description={
        trackId
          ? 'The user gets an email with a link to set a password. Access is granted before the mail goes out.'
          : 'The user receives an email with a link to set their password.'
      }
      footer={
        <>
          <Button variant="outline" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="invite-user-form" disabled={busy || !email.trim()}>
            {busy ? 'Sending…' : 'Send invite'}
          </Button>
        </>
      }
    >
      <form id="invite-user-form" className="space-y-4" onSubmit={submit}>
        <div className="space-y-2">
          <Label htmlFor="invite-email">Email</Label>
          <Input
            id="invite-email"
            type="email"
            autoComplete="off"
            required
            autoFocus
            readOnly={created !== null}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        {trackId && (
          <>
            <LevelSelect id="invite-level" value={level} onChange={setLevel} />
            <RoleCheckList
              roles={trackRoles ?? []}
              selected={roleIds}
              onToggle={(id, on) => setRoleIds((prev) => toggleInSet(prev, id, on))}
            />
          </>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
      </form>
    </Dialog>
  );
}
